import {
  CARD_RANKS,
  CARD_SUITS,
  CardRank,
  TableAgainst,
  TableCard,
  TableOutcome,
} from './types';

export function cardKey(card: TableCard): string {
  return `${card.rank}:${card.suit}`;
}

export function isValidCard(card: unknown): card is TableCard {
  if (!card || typeof card !== 'object') {
    return false;
  }
  const candidate = card as { rank?: unknown; suit?: unknown };
  return (
    (CARD_RANKS as readonly string[]).includes(String(candidate.rank)) &&
    (CARD_SUITS as readonly string[]).includes(String(candidate.suit))
  );
}

export function rankPips(rank: CardRank): number {
  if (rank === 'A') return 1;
  if (rank === 'K' || rank === 'Q' || rank === 'J' || rank === '10') return 10;
  return Number(rank);
}

/** Soft total: aces count as 11 when they do not bust (blackjack-lite). */
export function handTotal(cards: TableCard[]): number {
  let total = 0;
  let aces = 0;
  for (const card of cards) {
    if (card.rank === 'A') {
      aces += 1;
      total += 1;
    } else {
      total += rankPips(card.rank);
    }
  }
  while (aces > 0 && total + 10 <= 21) {
    total += 10;
    aces -= 1;
  }
  return total;
}

export function isBust(cards: TableCard[]): boolean {
  return handTotal(cards) > 21;
}

export function tableOutcome(playerHand: TableCard[], opponentHand: TableCard[]): TableOutcome {
  const player = handTotal(playerHand);
  const opponent = handTotal(opponentHand);
  if (player > 21) return 'lose';
  if (opponent > 21) return 'win';
  if (player > opponent) return 'win';
  if (player < opponent) return 'lose';
  return 'push';
}

export function fullDeck(): TableCard[] {
  const cards: TableCard[] = [];
  for (const suit of CARD_SUITS) {
    for (const rank of CARD_RANKS) {
      cards.push({ rank, suit });
    }
  }
  return cards;
}

export function shuffle<T>(items: T[], random: () => number = Math.random): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const item = next[i];
    const swap = next[j];
    if (item === undefined || swap === undefined) {
      continue;
    }
    next[i] = swap;
    next[j] = item;
  }
  return next;
}

export function cardsAreUnique(cards: TableCard[]): boolean {
  const keys = cards.map(cardKey);
  return new Set(keys).size === keys.length;
}

export function unusedCards(used: TableCard[], random: () => number = Math.random): TableCard[] {
  const usedKeys = new Set(used.map(cardKey));
  return shuffle(
    fullDeck().filter((card) => !usedKeys.has(cardKey(card))),
    random
  );
}

export function dealTableHands(random: () => number = Math.random): {
  playerHand: [TableCard, TableCard];
  opponentHand: [TableCard, TableCard];
} {
  const deck = shuffle(fullDeck(), random);
  const player1 = deck[0];
  const player2 = deck[1];
  const opponent1 = deck[2];
  const opponent2 = deck[3];
  if (!player1 || !player2 || !opponent1 || !opponent2) {
    throw new Error('Deck is too small to deal Table 21');
  }
  return {
    playerHand: [player1, player2],
    opponentHand: [opponent1, opponent2],
  };
}

/**
 * Draw the optional player hit, then auto-play the opponent: stand on 17+, hit below 17.
 */
export function drawTableHits(params: {
  playerHand: TableCard[];
  opponentHand: TableCard[];
  hit: boolean;
  random?: () => number;
}): { playerHit: TableCard | null; opponentHits: TableCard[] } {
  const deck = unusedCards([...params.playerHand, ...params.opponentHand], params.random);
  let index = 0;
  let playerHit: TableCard | null = null;
  if (params.hit) {
    playerHit = deck[index] ?? null;
    index += 1;
  }

  const opponentHits: TableCard[] = [];
  const opponentHand = [...params.opponentHand];
  while (handTotal(opponentHand) < 17) {
    const card = deck[index];
    index += 1;
    if (!card) {
      break;
    }
    opponentHits.push(card);
    opponentHand.push(card);
  }

  return { playerHit, opponentHits };
}

export function opponentHitsAreLegal(initialHand: TableCard[], hits: TableCard[]): boolean {
  const hand = [...initialHand];
  if (handTotal(hand) >= 17) {
    return hits.length === 0;
  }
  for (const card of hits) {
    if (handTotal(hand) >= 17) {
      return false;
    }
    hand.push(card);
  }
  return handTotal(hand) >= 17;
}

export function againstLabel(against: TableAgainst, opponentName?: string): string {
  if (against.type === 'house') {
    return 'the house';
  }
  return opponentName?.trim() || 'an opponent';
}
